import React, { useLayoutEffect, useRef, useState } from 'react';
import { renderStatusText } from '../CombatPlayerbar/statusWordGlyphs';
import { StatRow, statsToText } from './glyphs';
// The .combat-tt__* shell styles live in GameTip.css (extracted from
// CombatStatusRails.css per the tooltip-canon kill-list); import here so ANY
// consumer of the canonical tooltip (e.g. the simultaneous-combat entity
// tiles, which never mount the batch rails) gets the styles. Class RENAMING
// (.combat-tt__* → gt-*) is a separate, deferred kill-list pass.
import './GameTip.css';
// Canon-unify (2026-07-12): the combat tooltip panel co-wears the .pill-tooltip
// chrome so it reads as the same card as the drug node tooltip. Import the canon
// panel styles so ANY combat surface that renders a tooltip without a sibling
// PillCard still gets them (PillCard.css is otherwise only guaranteed present
// where a PillCard mounts).
import '../PillCard/PillCard.css';

const TOOLTIP_VIEWPORT_MARGIN = 12;

/** Return the smallest translation that keeps a floating rectangle on-screen. */
export function viewportShiftForRect(
  rect,
  viewportWidth,
  viewportHeight,
  margin = TOOLTIP_VIEWPORT_MARGIN,
) {
  if (!rect) return { x: 0, y: 0 };
  let x = 0;
  let y = 0;
  if (rect.left < margin) x += margin - rect.left;
  if (rect.right + x > viewportWidth - margin) x -= rect.right + x - (viewportWidth - margin);
  if (rect.top < margin) y += margin - rect.top;
  if (rect.bottom + y > viewportHeight - margin) y -= rect.bottom + y - (viewportHeight - margin);
  return { x: Math.round(x), y: Math.round(y) };
}

const CLIPPING_OVERFLOW = new Set(['auto', 'hidden', 'scroll', 'clip']);

/** The real visible rectangle: viewport intersected with every clipping ancestor. */
export function tooltipVisibleBounds(element, margin = TOOLTIP_VIEWPORT_MARGIN) {
  const viewport = {
    left: margin,
    top: margin,
    right: window.innerWidth - margin,
    bottom: window.innerHeight - margin,
  };
  let ancestor = element?.parentElement || null;
  while (ancestor && ancestor !== document.documentElement) {
    const style = window.getComputedStyle(ancestor);
    const clipsX = CLIPPING_OVERFLOW.has(style.overflowX) || CLIPPING_OVERFLOW.has(style.overflow);
    const clipsY = CLIPPING_OVERFLOW.has(style.overflowY) || CLIPPING_OVERFLOW.has(style.overflow);
    if (clipsX || clipsY) {
      const rect = ancestor.getBoundingClientRect();
      if (clipsX) {
        viewport.left = Math.max(viewport.left, rect.left + margin);
        viewport.right = Math.min(viewport.right, rect.right - margin);
      }
      if (clipsY) {
        viewport.top = Math.max(viewport.top, rect.top + margin);
        viewport.bottom = Math.min(viewport.bottom, rect.bottom - margin);
      }
    }
    ancestor = ancestor.parentElement;
  }
  return viewport;
}

export function viewportShiftForBounds(rect, bounds) {
  if (!rect || !bounds) return { x: 0, y: 0 };
  let x = 0;
  let y = 0;
  if (rect.left < bounds.left) x += bounds.left - rect.left;
  if (rect.right + x > bounds.right) x -= rect.right + x - bounds.right;
  if (rect.top < bounds.top) y += bounds.top - rect.top;
  if (rect.bottom + y > bounds.bottom) y -= rect.bottom + y - bounds.bottom;
  return { x: Math.round(x), y: Math.round(y) };
}

/**
 * Combat tooltips stay in their canonical DOM (so inherited status styling and
 * nested content remain intact), but receive one shared collision adjustment.
 * Measuring on host hover/focus also covers a tooltip embedded inside another
 * tooltip near an edge. No caller gets to invent its own viewport math.
 */
export function useTooltipViewportGuard(ref, enabled = true) {
  useLayoutEffect(() => {
    const floating = ref.current;
    if (!enabled || !floating || typeof window === 'undefined') return undefined;
    const host = floating.parentElement;
    if (!host) return undefined;
    let frame = null;

    const measure = () => {
      frame = null;
      // Measure from the unshifted position. Temporarily suppressing the CSS
      // transform transition is load-bearing: otherwise getBoundingClientRect()
      // can observe an in-flight, stale correction and the next correction
      // undershoots the viewport edge intermittently.
      const previousTransition = floating.style.getPropertyValue('transition');
      const previousTransitionPriority = floating.style.getPropertyPriority('transition');
      floating.style.setProperty('transition', 'none');
      floating.style.setProperty('--combat-tt-shift-x', '0px');
      floating.style.setProperty('--combat-tt-shift-y', '0px');
      const bounds = tooltipVisibleBounds(floating);
      floating.style.setProperty(
        '--combat-tt-available-width',
        `${Math.max(1, Math.floor(bounds.right - bounds.left))}px`,
      );
      floating.style.setProperty(
        '--combat-tt-available-height',
        `${Math.max(1, Math.floor(bounds.bottom - bounds.top))}px`,
      );
      const shift = viewportShiftForBounds(floating.getBoundingClientRect(), bounds);
      floating.style.setProperty('--combat-tt-shift-x', `${shift.x}px`);
      floating.style.setProperty('--combat-tt-shift-y', `${shift.y}px`);
      // Commit the corrected transform while transitions are disabled, then put
      // the component's authored reveal transition back exactly as it was.
      floating.getBoundingClientRect();
      if (previousTransition) {
        floating.style.setProperty('transition', previousTransition, previousTransitionPriority);
      } else {
        floating.style.removeProperty('transition');
      }
    };
    const schedule = () => {
      if (frame != null) {
        if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
        else window.clearTimeout(frame);
      }
      frame = typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(measure)
        : window.setTimeout(measure, 0);
    };

    host.addEventListener('mouseenter', schedule);
    host.addEventListener('pointerenter', schedule);
    host.addEventListener('focusin', schedule);
    host.addEventListener('pointerdown', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    const observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(schedule)
      : null;
    observer?.observe(floating);
    schedule();
    return () => {
      host.removeEventListener('mouseenter', schedule);
      host.removeEventListener('pointerenter', schedule);
      host.removeEventListener('focusin', schedule);
      host.removeEventListener('pointerdown', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      observer?.disconnect();
      if (frame != null) {
        if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
        else window.clearTimeout(frame);
      }
    };
  }, [enabled, ref]);
}

/* Shared rich combat tooltip on the compact gold-card substrate (PillCard look:
   solid dark card, a 3px accent stripe in the element's color, a 1:1 node-color
   circle holding the canonical icon, an ALL-CAPS name, and one or more labeled
   sections whose body lines render inline status-word glyphs). NO glassmorphism.

   Two ways to use it:
   - <CombatTooltip> wraps a trigger as `children` (the panel renders as a SIBLING
     of the trigger so the trigger's own textContent stays clean — important for
     the status-pill unit tests that assert exact value text). Used by the player
     status tags.
   - <CombatTooltipPanel> renders just the panel; drop it inside any element that
     already carries the `combat-tt-host` class (self-host). Used by EffectChip
     (equipped node pills) and the enemy strip pills, which are their own hosts.

   placement: 'above-right' (default — opens up-left, anchored to the trigger's
   right edge, for right-cluster pills), 'above-left' (opens up-right, for
   left-accumulating status tags), or 'below-left' (opens down-right, for the
   top-row enemy strip). */

export function CombatTooltipPanel({
  icon = null,
  name,
  subtitle = null,
  sections = [],
  placement = 'above-right',
  bare = false,
  inline = false,
  boss = false,
  wide = false,
  standalone = false,
  className = '',
}) {
  const panelRef = useRef(null);
  useTooltipViewportGuard(panelRef, !standalone);
  // A line is either a copy string (rendered through the status-word glyph
  // pass) or a `{ stats: [...] }` object rendered as a canonical glyph-stat
  // row (see GameTip/glyphs.jsx — the tooltip-canon grammar).
  const validSections = (sections || [])
    .map((s) => ({
      ...s,
      lines: (s?.lines || []).filter((l) => (
        l && typeof l === 'object'
          ? (l.stats || []).filter(Boolean).length > 0
          : String(l || '').trim()
      )),
    }))
    // `content` = a pre-rendered node (e.g. the enemy tooltip's Inflicts rows).
    .filter((s) => s.lines.length > 0 || s.label || s.content);

  // A TRUE ENEMY CARD is one whose body was composed by the shared enemy primitive
  // (`enemyTooltipSections`), which stamps `enemy: true` on the sections it returns.
  // Reading the marker here is what makes the canonical enemy chassis (the wide
  // desktop box in GameTip.css) automatic for EVERY enemy surface — hover rail,
  // announce card, solo HP bar, drug-card warden mini, Compendium page — instead of
  // each screen opting in and drifting. It is deliberately NOT a `:has(.gt-enemy-tt)`
  // CSS rule: an enemy mini nested inside a drug tooltip would widen the drug card.
  const isEnemyCard = validSections.some((s) => s.enemy);
  // A body whose embedded chips carry their OWN hover/focus tooltips (the outright
  // Engage / ANNOUNCE card — see ENEMY_CARD_RENDER_CONTEXT in GameTip/decode.jsx).
  // The panel has to know, because its default chassis is a SCROLL BOX (max-height +
  // overflow-y:auto) and a scroll box is a clipping ancestor: a nested popover would
  // be cut off at the card's edge and read as a chip that does nothing. Read off the
  // sections for the same reason `enemy` is — the composing primitive decides, not
  // whichever screen happens to mount the card.
  const hasInteractiveChips = validSections.some((s) => s.interactiveChips);

  const below = placement.startsWith('below');
  const alignLeft = placement.endsWith('left');

  // INLINE variant (Edward 2026-07-13): a simple status-tag hover collapsed to ONE
  // row -> the accent name on the LEFT, its concise def on the RIGHT,
  // reusing the split-header grammar. Renders only the FIRST section line as the def
  // (stat row or 0.78rem prose), so it matches the enemy tooltip's right-side
  // enumeration in both content and size. No stacked body.
  if (inline) {
    const firstLine = validSections[0]?.lines?.[0];
    return (
      <span
        ref={panelRef}
        className={[
          'pill-tooltip', 'combat-tt__panel', 'combat-tt__panel--inline',
          below ? 'combat-tt__panel--below' : '',
          alignLeft ? 'combat-tt__panel--left' : '',
        ].filter(Boolean).join(' ')}
        role="tooltip"
      >
        <span className="pill-tooltip__head combat-tt__head combat-tt__head--inline">
          <span className="pill-tooltip__titles">
            {name && <span className="pill-card__side-title combat-tt__name">{name}</span>}
            {firstLine != null && (
              <span className="combat-tt__inline-def">
                {typeof firstLine === 'object'
                  ? <StatRow items={firstLine.stats} />
                  : <span className="pill-card__hover-desc combat-tt__desc">{renderStatusText(firstLine)}</span>}
              </span>
            )}
          </span>
        </span>
      </span>
    );
  }

  return (
    <span
      ref={panelRef}
      className={[
        'pill-tooltip',
        'combat-tt__panel',
        below ? 'combat-tt__panel--below' : '',
        alignLeft ? 'combat-tt__panel--left' : '',
        bare ? 'combat-tt__panel--bare' : '',
        isEnemyCard ? 'combat-tt__panel--enemy' : '',
        hasInteractiveChips ? 'combat-tt__panel--interactive-chips' : '',
        boss ? 'combat-tt__panel--boss' : '',
        wide ? 'combat-tt__panel--wide' : '',
        standalone ? 'combat-tt__panel--standalone' : '',
        className,
      ].filter(Boolean).join(' ')}
      role={standalone ? 'group' : 'tooltip'}
    >
      {/* CANON-MATCH (2026-07-12): render the SAME inner structure as the drug
          node tooltip (.pill-tooltip__head with the node badge in the top-right
          corner + a titles column) so the combat tooltip reads identically. The
          .combat-tt__* names ride along as co-classes for back-compat (status-word
          sizing, GoldenEffectBanner's .combat-tt__node, the PlayerHtnPulseTag /
          DiuresisTag / CombatSummary tests' .combat-tt__name) — the canon classes
          own the look. */}
      {/* `bare` (status-tag hover, Edward 2026-07-12): drop the header entirely and
          show ONLY the effect — the same def glyph line an enemy tooltip renders
          when it inflicts this status. The name still rides in the host aria-label. */}
      {!bare && (icon || name) && (
        <span className="pill-tooltip__head combat-tt__head">
          {icon && (
            <span className="pill-tooltip__icon combat-tt__node" aria-hidden="true">{icon}</span>
          )}
          <span className="pill-tooltip__titles">
            {name && <span className="pill-card__side-title combat-tt__name">{name}</span>}
            {/* Canonical-entity subtitle — what this enemy represents medically
                (tooltip canon). Medical casing is load-bearing, so the --entity
                variant keeps it mixed-case under the name. */}
            {subtitle && (
              <span className="pill-tooltip__sub pill-tooltip__sub--entity combat-tt__subtitle">
                {subtitle}
              </span>
            )}
          </span>
        </span>
      )}
      <span className="pill-tooltip__body">
        {validSections.map((section, si) => (
          <span
            key={si}
            className={[
              'pill-tooltip__section',
              'combat-tt__section',
              section.augment ? 'combat-tt__section--augment' : '',
              section.keystone ? 'combat-tt__section--keystone' : '',
            ].filter(Boolean).join(' ')}
          >
            {section.label && (
              <span className="pill-card__side-eyebrow combat-tt__section-label">
                {renderStatusText(section.label)}
              </span>
            )}
            {section.content
              ? section.content
              : section.lines.map((line, li) => (
                line && typeof line === 'object'
                  ? <StatRow key={li} items={line.stats} />
                  : <span key={li} className="pill-card__hover-desc combat-tt__desc">{renderStatusText(line)}</span>
              ))}
          </span>
        ))}
      </span>
    </span>
  );
}

/* Host wrapper that adds CLICK-TOGGLE dismissal to any tooltip host. The
   panel reveals on pure CSS :hover/:focus — on touch, focus sticks to the
   host after the first tap, so without this a second tap could never close
   the tooltip. Logic: a click on a host whose tooltip was already open (the
   host held focus when the pointer went down) mutes the panel; the next
   click unmutes. Blur / mouse-leave reset the mute so plain desktop hover
   behavior stays exactly as before. */
export function TooltipToggleHost({
  as: As = 'span',
  className = '',
  onPointerDown,
  onClick,
  onBlur,
  onMouseLeave,
  children,
  ...rest
}) {
  const [muted, setMuted] = useState(false);
  const wasOpenRef = useRef(false);
  return (
    <As
      {...rest}
      className={[className, muted ? 'combat-tt-host--muted' : ''].filter(Boolean).join(' ')}
      onPointerDown={(e) => {
        // Read focus BEFORE the browser moves it for this click.
        const host = e.currentTarget;
        wasOpenRef.current = host === document.activeElement || host.contains(document.activeElement);
        onPointerDown?.(e);
      }}
      onClick={(e) => {
        setMuted((m) => (m ? false : wasOpenRef.current));
        onClick?.(e);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setMuted(false);
        onBlur?.(e);
      }}
      onMouseLeave={(e) => {
        setMuted(false);
        onMouseLeave?.(e);
      }}
    >
      {children}
    </As>
  );
}

export default function CombatTooltip({
  color,
  icon = null,
  name,
  subtitle = null,
  sections = [],
  placement = 'above-left',
  as: As = 'div',
  className = '',
  ariaLabel,
  bare = false,
  inline = false,
  boss = false,
  wide = false,
  // A second, self-contained panel revealed on the SAME hover, laid out beside the
  // primary (right on wide, stacked below on narrow). Opt-in: when omitted the
  // render path is byte-for-byte the single-panel behaviour every other caller
  // relies on. Used by the enemy-rail Diuresis tag to attach the Nephron figure.
  secondaryPanel = null,
  children,
}) {
  const pairRef = useRef(null);
  useTooltipViewportGuard(pairRef, Boolean(secondaryPanel));
  const ariaParts = [name, subtitle];
  (sections || []).forEach((s) => {
    (s?.lines || []).forEach((l) => {
      if (l && typeof l === 'object') {
        const text = statsToText(l.stats);
        if (text) ariaParts.push(text);
      } else if (String(l || '').trim()) {
        ariaParts.push(l);
      }
    });
  });
  const primaryPanel = (
    <CombatTooltipPanel icon={icon} name={name} subtitle={subtitle} sections={sections} placement={placement} bare={bare} inline={inline} boss={boss} wide={wide} />
  );
  // When a secondary panel is attached, both ride inside one absolutely-positioned
  // reveal wrapper (.combat-tt__pair) as static standalone cards laid out in a flex
  // row — so a width change in either can never split them and they wrap to a stack
  // on narrow viewports. The wrapper inherits the same below/above · left/right
  // anchoring the single panel uses.
  const below = placement.startsWith('below');
  const alignLeft = placement.endsWith('left');
  const body = secondaryPanel ? (
    <span
      ref={pairRef}
      className={[
        'combat-tt__pair',
        below ? 'combat-tt__pair--below' : 'combat-tt__pair--above',
        alignLeft ? 'combat-tt__pair--left' : 'combat-tt__pair--right',
      ].join(' ')}
    >
      <CombatTooltipPanel icon={icon} name={name} subtitle={subtitle} sections={sections} placement={placement} bare={bare} inline={inline} boss={boss} wide={wide} standalone />
      {secondaryPanel}
    </span>
  ) : primaryPanel;
  return (
    <TooltipToggleHost
      as={As}
      className={['combat-tt-host', 'combat-tt-host--inline', className].filter(Boolean).join(' ')}
      style={color ? { '--effect-color': color } : undefined}
      tabIndex={0}
      role="img"
      aria-label={ariaLabel || ariaParts.filter(Boolean).join(', ')}
    >
      {children}
      {body}
    </TooltipToggleHost>
  );
}
